import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

/**
 * 预定义主题色方案
 * main 为空格分隔的 RGB 值，供 css `rgb(var(--theme-50))` 使用
 * 所有渐变 / 浅色 / 透明效果均通过 `rgb(var(--theme-50) / 0.x)` 实现
 */
export const COLOR_THEMES = {
  violet:    { main: '139 92 246',  name: '紫罗兰' },
  iris:      { main: '99 102 241',  name: '鸢尾蓝' },
  ocean:     { main: '56 152 236',  name: '海洋蓝' },
  cyan:      { main: '6 182 212',   name: '晴空青' },
  emerald:   { main: '16 185 129',  name: '翡翠绿' },
  sakura:    { main: '236 112 146', name: '樱花粉' },
  coral:     { main: '248 113 113', name: '珊瑚红' },
  tangerine: { main: '251 146 60',  name: '橘子橙' },
  sunflower: { main: '234 179 8',   name: '向日葵' },
  flamingo:  { main: '217 70 239',  name: '火烈鸟' },
  berry:     { main: '192 38 211',  name: '浆果紫' },
  watermelon:{ main: '244 63 94',   name: '西瓜红' },
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // 从localStorage获取保存的主题，默认为light
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  const [colorTheme, setColorTheme] = useState(() => {
    const saved = localStorage.getItem('colorTheme');
    return saved && COLOR_THEMES[saved] ? saved : 'violet';
  });

  /** 将主题色写入 CSS 变量 */
  const applyColorTheme = useCallback((colorKey) => {
    const colors = COLOR_THEMES[colorKey] || COLOR_THEMES.violet;
    const root = document.documentElement;
    root.style.setProperty('--theme-50', colors.main);
  }, []);

  useEffect(() => {
    // 应用主题到document
    document.documentElement.setAttribute('data-theme', theme);
    // 保存到localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    applyColorTheme(colorTheme);
    localStorage.setItem('colorTheme', colorTheme);
  }, [colorTheme, applyColorTheme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    colorTheme,
    setColorTheme,
    colorThemes: COLOR_THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
